import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Button, Divider, CircularProgress, Chip,
  Stack, Avatar, IconButton, Tooltip,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventNoteIcon from '@mui/icons-material/EventNote';
import BusinessIcon from '@mui/icons-material/Business';
import CallIcon from '@mui/icons-material/Call';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StorefrontIcon from '@mui/icons-material/Storefront';
import NotesIcon from '@mui/icons-material/Notes';
import { followUpApi } from '../api/apiClient';
import PageShell from '../common/PageShell';
import { format } from 'date-fns';

const modeMeta = (mode = '') => {
  const m = mode.toUpperCase();
  if (m === 'VISITING')
    return { color: '#2563eb', bg: '#dbeafe', icon: <MeetingRoomIcon />, label: 'Visiting' };
  if (m === 'CALLING')
    return { color: '#7c3aed', bg: '#ede9fe', icon: <CallIcon />, label: 'Calling' };
  return { color: '#64748b', bg: '#f1f5f9', icon: <EventNoteIcon />, label: mode || 'Follow-Up' };
};

const InfoRow = ({ icon, label, value, color = 'primary.main' }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1.25 }}>
    <Avatar
      sx={{
        bgcolor: color,
        width: 36,
        height: 36,
        flexShrink: 0,
        '& svg': { fontSize: 18 },
      }}
    >
      {icon}
    </Avatar>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word', mt: 0.25 }}>
        {value || '—'}
      </Typography>
    </Box>
  </Stack>
);

const FollowUpView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    followUpApi.getById(id).then((res) => setData(res.data));
  }, [id]);

  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasCoords = data.latitude != null && data.longitude != null;
  const meta = modeMeta(data.mode);

  return (
    <PageShell
      icon={<EventNoteIcon />}
      title={`Follow-Up #${data.id}`}
      subtitle={data.companyName ? `Regarding ${data.companyName}` : 'Follow-up details'}
      breadcrumbs={[
        { label: 'Dashboard', to: '/' },
        { label: 'Follow-Ups', to: '/follow-ups' },
        { label: `#${data.id}` },
      ]}
      actions={
        <>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/follow-ups')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
            variant="outlined"
          >
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/follow-ups/edit/${id}`)}
            sx={{
              bgcolor: '#fff',
              color: '#1e3a8a',
              fontWeight: 700,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
            }}
          >
            Edit
          </Button>
        </>
      }
    >
      <Grid container spacing={3}>
        {/* LEFT: main content */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Summary card */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                sx={{ mb: 2.5 }}
              >
                <Avatar
                  sx={{
                    bgcolor: meta.bg,
                    color: meta.color,
                    width: 64,
                    height: 64,
                    '& svg': { fontSize: 30 },
                    boxShadow: `0 8px 20px ${meta.color}33`,
                  }}
                >
                  {meta.icon}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                      {meta.label}
                    </Typography>
                    {data.status && (
                      <Chip
                        label={data.status}
                        size="small"
                        color={data.status === 'PENDING' ? 'warning' : 'success'}
                        sx={{ fontWeight: 700 }}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {data.companyName || 'Company not linked'}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ mb: 1 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InfoRow
                    icon={<BusinessIcon />}
                    label="Company"
                    value={data.companyName}
                    color="#2563eb"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoRow
                    icon={<CalendarTodayIcon />}
                    label="Follow-Up Date"
                    value={
                      data.followUpDate
                        ? format(new Date(data.followUpDate), 'PPpp')
                        : '—'
                    }
                    color="#7c3aed"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoRow
                    icon={<AccessTimeIcon />}
                    label="Created At"
                    value={
                      data.createdAt
                        ? format(new Date(data.createdAt), 'PPpp')
                        : '—'
                    }
                    color="#0891b2"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoRow
                    icon={<EventNoteIcon />}
                    label="Record ID"
                    value={`#${data.id}`}
                    color="#ea580c"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Description */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 36,
                    height: 36,
                    '& svg': { fontSize: 18 },
                  }}
                >
                  <NotesIcon />
                </Avatar>
                <Typography variant="subtitle1" fontWeight={700}>
                  Description
                </Typography>
              </Stack>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
                >
                  {data.description || 'No description provided.'}
                </Typography>
              </Paper>
            </Paper>
          </Stack>
        </Grid>

        {/* RIGHT: sidebar */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3} sx={{ position: { md: 'sticky' }, top: { md: 24 } }}>
            {/* At a glance */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                  fontSize: 11,
                  mb: 2,
                }}
              >
                At a Glance
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Mode
                  </Typography>
                  <Chip
                    label={meta.label}
                    size="small"
                    sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 700 }}
                  />
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {data.status || '—'}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {hasCoords ? 'Captured' : 'Not captured'}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Record ID
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    #{data.id}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            {/* Location */}
            {hasCoords && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'success.main',
                      width: 36,
                      height: 36,
                      '& svg': { fontSize: 18 },
                    }}
                  >
                    <LocationOnIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Location
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {Number(data.latitude).toFixed(5)}, {Number(data.longitude).toFixed(5)}
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  href={`https://www.google.com/maps?q=${data.latitude},${data.longitude}`}
                  target="_blank"
                  rel="noopener"
                >
                  Open in Maps
                </Button>
              </Paper>
            )}

            {/* Quick actions */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                  fontSize: 11,
                  mb: 2,
                }}
              >
                Quick Actions
              </Typography>
              <Stack spacing={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/follow-ups/edit/${id}`)}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Edit Follow-Up
                </Button>
                {data.marketingId && (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<StorefrontIcon />}
                    onClick={() => navigate(`/marketing/view/${data.marketingId}`)}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    View Company
                  </Button>
                )}
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/follow-ups')}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Back to List
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </PageShell>
  );
};

export default FollowUpView;